import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { format } from 'date-fns';
import { Observable, Subscription } from 'rxjs';
import { ContratoService } from 'src/app/contrato/contrato.service';
import { PerfilService } from 'src/app/perfil/perfil.service';
import { AutenticacaoService } from 'src/app/seguranca/autenticacao.service';
import { StatusParcelaEnum } from 'src/app/shared/enums/status-parcela.enum';
import { LoaderService } from 'src/app/shared/loader/loader.service';
import { Contrato } from 'src/app/shared/models/contrato.model';
import { EnvioDeEmailBoletoRenegociacoRequest } from 'src/app/shared/models/enviar-email-boleto-request.model';
import { EnvioSmsBoletoRequest } from 'src/app/shared/models/enviar-sms-boleto-request.model';
import { OrigemPlanoEnum, TipoPlanoEnum } from 'src/app/shared/models/novo-plano-renegociacao.model';
import PerfilCliente from 'src/app/shared/models/perfil-cliente.model';
import { PlanoEfetivado } from 'src/app/shared/models/plano-efetivado.model';
import { BaseModalService } from 'src/app/shared/services/base-modal.service';
import { GoogleAnalyticsService } from 'src/app/shared/services/google-analytics.service';
import { InsiderPageType, InsiderTagService } from 'src/app/shared/services/insider-tag.service';
import { ToastService } from 'src/app/shared/toast';
import DateUtils from 'src/app/shared/utils/date-utils';

@Component({
  selector: 'app-checkout-boleto-renegociacao',
  templateUrl: './checkout-boleto-renegociacao.component.html',
  styleUrls: ['./checkout-boleto-renegociacao.component.scss']
})
export class CheckoutBoletoRenegociacaoComponent implements OnInit, OnDestroy {

  isMobileMatches: Observable<BreakpointState> = this.breakpointObserver.observe('(max-width: 768px)');
  idContrato: string;
  subscriptions = new Subscription();
  contrato: Contrato;
  disabledEmail: boolean;
  disabledSms: boolean;
  perfilUsuario: PerfilCliente | null = null;
  planoEfetivado: PlanoEfetivado;

  constructor(
    public breakpointObserver: BreakpointObserver,
    private router: Router,
    private route: ActivatedRoute,
    private autenticacaoService: AutenticacaoService,
    private contratoService: ContratoService,
    private loader: LoaderService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private toast: ToastService,
    private perfilService: PerfilService,
    private baseModalService: BaseModalService,
    private insiderTagService: InsiderTagService,
  ) { }

  ngOnInit(): void {
    this.subscriptions.add(this.route.params.subscribe(({ idContrato }) => {
      this.idContrato = idContrato;
      this.init(idContrato)
    }));
  }

  ngOnDestroy(): void {
    this.insiderTagService.clearPageObject();
  }

  async init(idContrato: string) {
    this.loader.start();
    await this.buscarContrato(idContrato);
    await this.buscarBoleto(idContrato);
    await this.buscarClientePerfil();
    this.enviarEventoGTM();
    this.loader.stop();
  }

  private async buscarBoleto(idContrato: string): Promise<void> {
    try {
      this.planoEfetivado = await this.contratoService.buscarPlanoEfetivado(idContrato);
      this.insiderTagService.initializeCheckoutPage(InsiderPageType.CONFIRMATION, `boleto-renegociacao-${idContrato}-${this.planoEfetivado.quantidadeParcelas}`);

      if (this.planoEfetivado.origem === OrigemPlanoEnum.ESPECIAL || this.planoEfetivado.origem === OrigemPlanoEnum.MASSIVO) {
        this.googleAnalyticsService.pushNavigateEvent('/renegociacao/contratos/renegociacao/oferta-especial/boletos');
      } else {
        this.googleAnalyticsService.pushNavigateEvent('/renegociacao/contratos/renegociacao/boletos');
      }
    } catch (error) {
      this.toast.show({ type: 'error', title: 'Erro ao buscar o boleto da entrada.', text: 'Por favor tente novamente mais tarde.' });
    }
  }

  private async buscarContrato(id: string): Promise<void> {
    try {
      this.contrato = await this.contratoService.getContrato(id);
    } catch (error) {
      this.toast.show({ type: 'error', title: 'Erro ao buscar o contrato.', text: 'Por favor tente novamente mais tarde.' });
    }
  }

  enviarEventoGTM() {
    const nomePagina = this.planoEfetivado?.origem === OrigemPlanoEnum.ESPECIAL ? '/renegociacao/contratos/renegociacao/oferta-especial/boletos' : '/renegociacao/contratos/renegociacao/boletos';

    this.googleAnalyticsService.pushEvent({
      event: 'update',
      pagina: {
        nomePagina,
        templatePagina: 'renegociacao',
        contratos: {
          numeroContrato: this.contrato.contrato,
          dataVencimento: format(DateUtils.normalizedDate(this.contrato.vencimento), 'dd/MM/yyyy'),
          saldo: this.contrato.saldo,
          parcelasPagas: this.contrato.parcelas.reduce((acc, parcela) => parcela.statusParcela === StatusParcelaEnum.STATUS_PAGO ? acc += 1 : acc, 0),
          situacao: this.googleAnalyticsService.situacaoContratoPipe(this.contrato.situacao),
          mercadoria: this.contrato?.mercadoria?.mercadorias.join(' / '),
        }
      }
    });
  }

  async copiarCodigoBoleto() {
    try {
      await navigator.clipboard.writeText(this.planoEfetivado.linhaDigitavel);
      this.toast.show({ type: 'success', title: 'Código copiado!' });
    } catch (err) {
      this.toast.show({ type: 'error', title: 'Erro ao Copiar', text: 'Desculpe, não conseguimos copiar o texto para a área de transferência. Por favor, tente novamente.' });
    }
  }

  async buscarClientePerfil() {
    try {
      const clientePerfil = await this.perfilService.getPerfil();

      this.perfilUsuario = clientePerfil ?? null;

      if (this.perfilUsuario?.celular) {
        this.perfilUsuario.celular = this.perfilUsuario?.celular?.substring(3);
      }
    } catch (error) {
      this.perfilUsuario = null;
    }
  }

  maskPhoneNumber(phoneNumber: string) {
    let formattedNumber = phoneNumber.replace(/(\d{2})(\d{4})(\d{5})/, "($1) $2-$3");
    return formattedNumber.replace(/^(.{7})(.{4})/, '$1*** - ');
  }

  async showNoContactDataModal(type: 'EMAIL' | 'SMS') {
    const contactType = type === 'EMAIL' ? 'E-mail' : 'Número de celular';

    return await this.baseModalService.showConfirmationModal({
      text: `${contactType} não cadastrado. <strong>Atualize o ${contactType.toLocaleLowerCase()} no seu perfil.</strong>`,
      confirmBtnText: 'Ir para o meu perfil',
      deniedBtnText: 'Agora não',
    }).afterClosed().toPromise();
  }

  async sendInvoicePaymentCode(type: 'SMS' | 'EMAIL') {
    if ((type === 'EMAIL' && !this.perfilUsuario?.email) || (type === 'SMS' && !this.perfilUsuario?.celular)) {
      const confirm = await this.showNoContactDataModal(type);

      if (confirm) {
        this.router.navigate([`/portal/perfil`]);
      }

      return;
    }

    const confirm = await this.baseModalService.showConfirmationModal({
      text: `Enviaremos o código do boleto para o ${type} cadastrado: <br/> <strong>${type === 'SMS' ? this.maskPhoneNumber(this.perfilUsuario.celular) : this.perfilUsuario.email}</strong>`,
      confirmBtnText: 'Enviar',
      deniedBtnText: 'Cancelar',
    }).afterClosed().toPromise();

    if (confirm) {
      return type === 'EMAIL' ? this.enviaBoletoEmail() : this.enviaBoletoSms();
    }
  }

  private async enviaBoletoEmail() {
    try {
      this.loader.start();
      const enviaEmail: EnvioDeEmailBoletoRenegociacoRequest = {
        bandeira: this.autenticacaoService.bandeira,
        contrato: this.idContrato,
        email: this.perfilUsuario.email,
      };
      this.disabledEmail = true;
      await this.contratoService.enviarBoletoEmailRenegociacao(enviaEmail);
      this.toast.show({ type: 'success', title: `Código do boleto enviado por e-mail com sucesso!` });
    } catch (error) {
      this.toast.show({ type: 'error', title: 'Erro ao enviar boleto por e-mail.', text: 'Por favor tente novamente mais tarde.' });
    } finally {
      this.loader.stop();
    }
  }

  private async enviaBoletoSms() {

    try {
      this.loader.start();
      const enviaSms: EnvioSmsBoletoRequest = {
        bandeira: this.autenticacaoService.bandeira,
        contrato: this.idContrato,
        phone: this.perfilUsuario.celular,
      };

      await this.contratoService.enviarBoletoSmsRenegociacao(enviaSms);
      this.disabledSms = true;
      this.toast.show({ type: 'success', title: `Código do boleto enviado por sms com sucesso!` });
    } catch (error) {
      this.toast.show({ type: 'error', title: 'Erro ao enviar boleto por sms.', text: 'Por favor tente novamente mais tarde.' });
    } finally {
      this.loader.stop();
    }
  }

  async downloadBoleto(): Promise<void> {
    try {
      this.loader.start();

      if (this.planoEfetivado.origem === OrigemPlanoEnum.ESPECIAL) {
        return await this.downloadBoletoOfertaEspecial();
      }

      if (this.planoEfetivado.origem === OrigemPlanoEnum.MASSIVO) {
        return await this.contratoService.downloadBoletoNovoMassivo(this.idContrato);
      }

      await this.contratoService.downloadBoletoRenegociacao(this.idContrato);
      this.toast.show({ type: 'success', title: `Download do boleto gerado com sucesso!` });
    } catch (error) {
      this.toast.show({ type: 'error', title: 'Erro ao fazer download do boleto', text: 'Por favor tente novamente mais tarde.' });
    } finally {
      this.loader.stop();
    }
  }

  async downloadBoletoOfertaEspecial(): Promise<void> {
    await this.contratoService.downloadBoletoRenegociacaoMassiva(this.idContrato, this.planoEfetivado.tipo === TipoPlanoEnum.QUITACAO);
  }

  async downlaodBoletoPlanoComum(): Promise<void> {
    await this.contratoService.downloadBoletoRenegociacao(this.idContrato);
  }

  get isAutoLogin() {
    return this.autenticacaoService.isAutoLogin;
  }

  get hideShareActions() {
    return this.isAutoLogin || this.perfilUsuario === null;
  }
}

